import {FC, useEffect, useState, FocusEvent} from 'react';
import {toast} from 'react-toastify';
//mui
import {
  Box,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
//store
import {getCurrentEventSelector} from '@/store/organization-event';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getAuctionCategoriesSelector, getAuctionList, getAuctionListSelector} from '@/store/organization-auction';
//components
import Loader from '@/shared/components/Loader';
import Pagination from '@/shared/components/Pagination';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
import ActionsMenu from '@/shared/components/Table/ActionMenu';
import SearchHeader from '@/shared/components/Table/SearchHeader';
import Button from '@/shared/components/Button';
import Input from '@/shared/components/Inputs/Input';
import Modal from '@/shared/components/Modal';
import AuctionDateInfo from './AuctionDateInfo';
import AuctionItem from './AuctionItem';
import AuctionDetails from './AuctionDetails';
//types
import {
  IFilterGroup,
  IOrganizationAuctionCategory,
  IOrganizationAuctionListData,
  IOrganizationAuctionResponse,
} from '@/utils/types';
//helpers
import {camelize, getErrorMessage, getTableStatusStyle} from '@/utils/helpers';
import {
  getUpdatedFilterData,
  handleSetCategoryIds,
  tableActionMenuContent,
  updatedAuctionFilterOptions,
  getUpdatedAuctionValues,
  getUpdatedAuctionStatusValues,
  auctionDefaultFilterData,
  auctionDefaultFilterValues,
} from './helpers';
//icons
import {ReactComponent as UpDownIcon} from '@/assets/icons/arrow-up-down.svg';
import {ReactComponent as EllipseIcon} from '@/assets/icons/ellipse-green.svg';
import {ReactComponent as PlusIcon} from '@/assets/icons/plus.svg';
import {ReactComponent as DangerIcon} from '@icons/danger.svg';
import {ReactComponent as JudgeIcon} from '@icons/sidebar/judge.svg';

//services
import {OrganizationAuctionService} from '@/utils/services';
//constants
import {
  BODY1,
  DOLLAR,
  ID,
  RIGHT,
  STATUS,
  defaultPage,
  defaultPageSize,
  ITEM,
  AUCTION,
  VALUE,
  MIN_BID,
  MIN_BID_CAMEL,
  BID_INCREMENT,
  BID_INCREMENT_CAMEL,
  BUY_NOW_PRICE,
  BUY_NOW_PRICE_CAMEL,
  IMAGE_TABLE_ALT,
  SUBTITLE1,
  START,
  ACTIVE,
  ADD_ITEM,
  SEARCH_ITEM,
  ACTIVITY,
  CATEGORIES,
  ALL_CATEGORIES,
  VALUE_LOWER,
  CLOSE_UPPER,
  ERROR,
  DELETE,
  DELETE_ITEM,
  DELETE_ITEM_DESCRIPTION,
  CANCEL,
  ADD_NEW_ITEM,
  EDIT_ITEM,
  DELETE_UPPER,
} from '@/utils/constants';
//enums
import {AUCTION_ACTIVITY, AUCTION_TYPE, MENU_OPTION, PAYMENT_STATUS} from '@/utils/enums';
//hooks
import {useSkipFirstRenderEffect} from '@/utils/hooks';
//styles
import {auctionTableStyle} from './AuctionTable.styles';
import {actionsMenuStyle} from '@/shared/components/Table/ActionMenu/ActionsMenu.styles';
import {orgAPI} from '@/core/api';



const AuctionTable = () => {
  const dispatch = useAppDispatch();

  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(defaultPage);
  const [searchName, setSearchName] = useState<string>('');

  const [deleteAuctionId, setDeleteAuctionId] = useState<number | null>(null);
  const [editableAuction, setEditableAuction] = useState<IOrganizationAuctionResponse | null>(null);

  const [filterData, setFilterData] = useState<IFilterGroup[]>(auctionDefaultFilterData);
  const [filterValues, setFilterValues] = useState(auctionDefaultFilterValues);

  const [isShowAuctionModal, setIsShowAuctionModal] = useState<boolean>(false);
  const [isShowAuctionDetailsModal, setIsShowAuctionDetailsModal] = useState<boolean>(false);
  const [isProfilePage, setIsProfilePage] = useState<boolean>(false);

  const currentEvent = useAppSelector(getCurrentEventSelector);
  const auctionList = useAppSelector(getAuctionListSelector);
  const auctionCategories = useAppSelector(getAuctionCategoriesSelector);

  useSkipFirstRenderEffect(() => {
    if (currentEvent) getAuctionListData();
  }, [currentPage, filterValues]);

  const isFilteredData = filterValues !== auctionDefaultFilterValues || !!searchName;

  useEffect(() => {
    if (auctionCategories) setFilterData(updatedAuctionFilterOptions(auctionCategories, filterData));
  }, [auctionCategories]);

  const handleProfileTabStyles = (isProfilePage: boolean) => setIsProfilePage(isProfilePage);

  const handleAuctionModalChange = async (tableId?: number) => {
    if (tableId && !isShowAuctionModal) {
      const currentAuction = await OrganizationAuctionService.getAuction(tableId, orgAPI);
      setEditableAuction(currentAuction.data);
    } else if (editableAuction) setEditableAuction(null);

    setIsShowAuctionModal(!isShowAuctionModal);
    if (isShowAuctionModal) getAuctionListData();
  };

  const handleOpenAuctionDetailsModal = async (tableId?: number) => {

    if (tableId && !isShowAuctionDetailsModal) {
      const currentAuction = await OrganizationAuctionService.getAuction(tableId, orgAPI);
      setEditableAuction(currentAuction.data);
    } else if (editableAuction) setEditableAuction(null);

    setIsShowAuctionDetailsModal(!isShowAuctionDetailsModal);
    if (isShowAuctionDetailsModal) getAuctionListData();
  };

  const getAuctionListData = async (value?: string) => {
    setIsDataLoaded(false);

    try {
      const reqBody = {
        eventId: currentEvent!.id,
        page: currentPage,
        searchName: value,
        ...filterValues,
      };

      await dispatch(getAuctionList(reqBody)).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsDataLoaded(true);
    }
  };

  const updateAuctionValue = async (
    event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    auctionId: number,
    rowName: string
  ) => {
    try {
      const currentAuction = await OrganizationAuctionService.getAuction(auctionId, orgAPI);
      const updatedAuctionValues = getUpdatedAuctionValues(
        currentAuction.data,
        rowName,
        event.target.value,
        currentEvent!.id
      );
      await OrganizationAuctionService.updateAuction(auctionId, updatedAuctionValues);
    } catch (error) {
      toast.error(getErrorMessage(error));
      await getAuctionListData();
    }
  };

  const handleSortChange = (newSortColumn: string) => () => {
    const isAsc = filterValues.sortColumn !== newSortColumn || !filterValues.isOrderDesc;

    setFilterValues((prevState) => ({
      ...prevState,
      isOrderDesc: isAsc,
      sortColumn: newSortColumn,
    }));
  };

  const handleSearchChange = (value: string) => {

    setSearchName(value);
    if (currentEvent) getAuctionListData(value);
  };

  const handlePressEnter = () => {
    setCurrentPage(defaultPage);
    if (currentEvent) getAuctionListData(searchName);
  };

  const getPayStatusSx = (payStatus: string) => getTableStatusStyle(payStatus);

  const updateFilterData = (groupTitle: string, optionTitle: string, id?: number) => {
    if (optionTitle === ALL_CATEGORIES) {
      return setFilterData(
        updatedAuctionFilterOptions(auctionCategories as IOrganizationAuctionCategory[], filterData)
      );
    }

    return setFilterData((prevFilterData) => getUpdatedFilterData(prevFilterData, groupTitle, optionTitle, id));
  };

  const handleFilterChange = (groupTitle: string, optionTitle: string, id?: number) => {
    updateFilterData(groupTitle, optionTitle, id);

    switch (groupTitle) {
      case ACTIVITY:
        setFilterValues((prevState) => ({
          ...prevState,
          activity: camelize(optionTitle),
        }));
        break;

      case AUCTION:
        setFilterValues((prevState) => ({
          ...prevState,
          auctionType: camelize(optionTitle),
        }));
        break;

      case CATEGORIES:
        const newCategoryIds = handleSetCategoryIds(filterValues.categoryIds, id);
        setFilterValues({
          ...filterValues,
          categoryIds: newCategoryIds as null,
        });

        break;

      default:
        break;
    }
  };

  const handleDeleteAuctionItem = async () => {
    try {
      await OrganizationAuctionService.deleteAuction(deleteAuctionId as number);
      await getAuctionListData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeleteAuctionId(null);
    }
  };

  const handleChangeStatusAuctionItem = async (auctionId: number, item: IOrganizationAuctionListData) => {
    if (item?.paymentStatus === PAYMENT_STATUS.PAID) return;

    try {
      const currentAuction = await OrganizationAuctionService.getAuction(auctionId, orgAPI);
      const updatedAuctionStatusValues = getUpdatedAuctionStatusValues(currentAuction.data);
      await OrganizationAuctionService.updateAuction(auctionId, updatedAuctionStatusValues);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      await getAuctionListData();
    }
  };

  const handleClickMenuOption = (option: string, item: IOrganizationAuctionListData) => async () => {
    switch (option) {
      case MENU_OPTION.DELETE:
        setDeleteAuctionId(item.id);
        break;

      case MENU_OPTION.CLOSE:
      case MENU_OPTION.ACTIVE:
        handleChangeStatusAuctionItem(item.id, item);
        break;

      case MENU_OPTION.EDIT:
        handleAuctionModalChange(item.id);
        break;

      case MENU_OPTION.DETAILS:
        handleOpenAuctionDetailsModal(item.id);
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    setCurrentPage((prev) => (prev === 1 ? prev : 1));
  }, [searchName, filterValues]);

  return (
    <Box sx={auctionTableStyle.wrapper}>
      <AuctionDateInfo />

      <Box sx={auctionTableStyle.content}>
        <SearchHeader
          page={currentPage}
          items={auctionList!.totalCount < defaultPageSize ? auctionList!.totalCount : defaultPageSize}
          total={auctionList?.totalCount}
          searchPlaceholder={SEARCH_ITEM}
          onPressEnter={handlePressEnter}
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
          filterOption={filterData}
          parentClassName={{padding: '0 16px'}}
          actionButton={
            <Button
              onClick={() => handleAuctionModalChange()}
              title={ADD_NEW_ITEM}
              startIcon={<PlusIcon />}
              sx={auctionTableStyle.searchButton}
            />
          }
        />

        {!!auctionList?.totalCount && isDataLoaded ? (
          <Box sx={auctionTableStyle.tableContent}>
            <TableContainer sx={auctionTableStyle.container}>
              <Table sx={auctionTableStyle.table}>
                <TableHead>
                  <TableRow sx={auctionTableStyle.tableHeader}>
                    <TableCell>
                      <TableSortLabel active onClick={handleSortChange(ID)} IconComponent={UpDownIcon}>
                        {ID}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>{ITEM}</TableCell>

                    <TableCell>{AUCTION}</TableCell>

                    <TableCell align={RIGHT}>
                      <TableSortLabel active onClick={handleSortChange(VALUE)} IconComponent={UpDownIcon}>
                        {VALUE}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell align={RIGHT}>
                      <TableSortLabel active onClick={handleSortChange(MIN_BID_CAMEL)} IconComponent={UpDownIcon}>
                        {MIN_BID}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell align={RIGHT}>
                      <TableSortLabel active onClick={handleSortChange(BID_INCREMENT_CAMEL)} IconComponent={UpDownIcon}>
                        {BID_INCREMENT}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell align={RIGHT}>
                      <TableSortLabel active onClick={handleSortChange(BUY_NOW_PRICE_CAMEL)} IconComponent={UpDownIcon}>
                        {BUY_NOW_PRICE}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell align={RIGHT}>{STATUS}</TableCell>

                    <TableCell />
                  </TableRow>
                </TableHead>

                <TableBody>
                  {auctionList?.data.map((row) => (
                    <TableRow key={row.id} sx={auctionTableStyle.tableBody}>
                      <TableCell>{row.id}</TableCell>

                      <TableCell>
                        <Box sx={auctionTableStyle.bodyCell}>
                          <Box sx={auctionTableStyle.bodyCellImage}>
                            {row.photo ? <img src={row.photo} alt={IMAGE_TABLE_ALT} /> : <JudgeIcon />}
                          </Box>
                          <Box sx={auctionTableStyle.bodyCellImageContent}>
                            <Typography variant={SUBTITLE1} sx={auctionTableStyle.bodyCellImageContentName}>
                              {row.name}
                            </Typography>
                            <Typography variant={BODY1} sx={auctionTableStyle.bodyCellImageContentCategory}>
                              {row.category}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>{row.isLiveAuction ? AUCTION_TYPE.LIVE : AUCTION_TYPE.SILENT}</TableCell>

                      <TableCell align={RIGHT}>
                        <Input
                          defaultValue={row.value}
                          onBlur={(event) => updateAuctionValue(event, row.id, VALUE_LOWER)}
                          InputProps={{
                            startAdornment: <InputAdornment position={START}>{DOLLAR}</InputAdornment>,
                          }}
                        />
                      </TableCell>

                      <TableCell align={RIGHT}>
                        <Input
                          defaultValue={row.minBid}
                          onBlur={(event) => updateAuctionValue(event, row.id, MIN_BID_CAMEL)}
                          InputProps={{
                            startAdornment: <InputAdornment position={START}>{DOLLAR}</InputAdornment>,
                          }}
                        />
                      </TableCell>

                      <TableCell align={RIGHT}>
                        <Input
                          defaultValue={row.bidIncrement}
                          onBlur={(event) => updateAuctionValue(event, row.id, BID_INCREMENT_CAMEL)}
                          InputProps={{
                            startAdornment: <InputAdornment position={START}>{DOLLAR}</InputAdornment>,
                          }}
                        />
                      </TableCell>

                      <TableCell align={RIGHT}>
                        <Input
                          defaultValue={row.buyNowPrice}
                          onBlur={(event) => updateAuctionValue(event, row.id, BUY_NOW_PRICE_CAMEL)}
                          InputProps={{
                            startAdornment: <InputAdornment position={START}>{DOLLAR}</InputAdornment>,
                          }}
                        />
                      </TableCell>

                      <TableCell align={RIGHT}>
                        <Typography
                          sx={getPayStatusSx(
                            row.status === AUCTION_ACTIVITY.ACTIVE ? AUCTION_ACTIVITY.ACTIVE : AUCTION_ACTIVITY.CLOSED
                          )}
                        >
                          {row.status === AUCTION_ACTIVITY.ACTIVE ? AUCTION_ACTIVITY.ACTIVE : AUCTION_ACTIVITY.CLOSED}
                          <EllipseIcon />
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <ActionsMenu
                          disableRipple
                          content={
                            <Box>
                              {tableActionMenuContent.map((item: string) => {
                                if (row.status === AUCTION_ACTIVITY.ACTIVE && item === ACTIVE) return null;
                                if (row.status === AUCTION_ACTIVITY.CLOSED && item === CLOSE_UPPER) return null;

                                return (
                                  <Typography
                                    key={item}
                                    onClick={handleClickMenuOption(item, row)}
                                    variant={BODY1}
                                    sx={{
                                      ...auctionTableStyle.menuItem,
                                      ...(item === DELETE_UPPER &&
                                        row.paymentStatus === PAYMENT_STATUS.PAID &&
                                        actionsMenuStyle.disabledMenuItem),
                                      ...(item === ACTIVE &&
                                        row.paymentStatus === PAYMENT_STATUS.PAID &&
                                        actionsMenuStyle.disabledMenuItem),
                                    }}
                                  >
                                    {item}
                                  </Typography>
                                );
                              })}
                            </Box>
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={auctionTableStyle.pagination}>
              <Pagination
                page={currentPage}
                pageSize={auctionList?.pageSize}
                total={auctionList?.totalCount}
                onChange={(step) => setCurrentPage(step)}
              />
            </Box>
          </Box>
        ) : (
          !!auctionList?.data && isFilteredData && <EmptyTableData />
        )}

        <Modal
          // onClose={handleAuctionModalChange}
          onClose={() => handleAuctionModalChange()}
          open={isShowAuctionModal}
          sx={auctionTableStyle.auctionModal}
          title={editableAuction ? EDIT_ITEM : ADD_ITEM}
        >
          <AuctionItem onCloseModal={handleAuctionModalChange} editableAuction={editableAuction} />
        </Modal>

        <Modal
          // onClose={handleOpenAuctionDetailsModal}
          onClose={() => handleOpenAuctionDetailsModal()}
          open={isShowAuctionDetailsModal}
          sx={{...auctionTableStyle.detailsModal, ...(isProfilePage && auctionTableStyle.profilePage)}}
        >
          <AuctionDetails
            onCloseModal={handleOpenAuctionDetailsModal}
            editableAuction={editableAuction}
            handleProfileTabStyles={handleProfileTabStyles}
          />
        </Modal>

        <Modal
          onClose={() => setDeleteAuctionId(null)}
          onSave={handleDeleteAuctionItem}
          open={!!deleteAuctionId}
          sx={auctionTableStyle.deleteAuctionModal}
          color={ERROR}
          closeButtonTitle={CANCEL}
          mainButtonTitle={DELETE}
          isShowCloseButton
          isShowSaveButton
          mainIcon={<DangerIcon />}
          title={DELETE_ITEM}
          description={DELETE_ITEM_DESCRIPTION}
        />

        {!isDataLoaded && <Loader parentClassName={auctionTableStyle.loader} size={50} />}
      </Box>
    </Box>
  );
};

export default AuctionTable;
